import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, MessageCircle as ChatCircle, CheckCircle, Clock, ArrowLeft, IndianRupee, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STEPS = [
  { key: "pending", label: "Booking placed" },
  { key: "confirmed", label: "Provider confirmed" },
  { key: "on_the_way", label: "On the way" },
  { key: "completed", label: "Completed" },
];

export default function TrackBooking() {
  const { id } = useParams();
  const [b, setB] = useState(null);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const r = await api.get(`/bookings/${id}/track`);
        if (mounted) setB(r.data);
      } catch {}
    };
    tick();
    const iv = setInterval(tick, 4000);
    return () => { mounted = false; clearInterval(iv); };
  }, [id]);

  const setStatus = async (status, coords) => {
    await api.patch(`/bookings/${id}`, { status, ...(coords || {}) });
    const r = await api.get(`/bookings/${id}/track`);
    setB(r.data);
  };

  const markPaid = async () => {
    const remaining = (b.charges || 0) - (b.amount_paid || 0);
    if (remaining <= 0) { toast.info("Already fully paid"); return; }
    if (!confirm(`Confirm payment of ₹${remaining.toLocaleString("en-IN")}?`)) return;
    try {
      await api.patch(`/bookings/${id}/payment`, {
        amount_paid: remaining,
        payment_ref: `demo_${Date.now()}`,
        payment_status: "paid",
      });
      toast.success("Payment marked as paid");
      const r = await api.get(`/bookings/${id}/track`);
      setB(r.data);
    } catch (e) {
      toast.error("Could not update payment");
    }
  };

  const payMethodLabel = { advance: "Advance (full)", partial: "Partial (deposit + COD)", pay_later: "Pay later", cod: "Cash on service" };
  const payStatusStyle = {
    unpaid: "bg-amber-100 text-amber-800",
    partially_paid: "bg-orange-100 text-orange-800",
    paid: "bg-emerald-100 text-emerald-800",
    refunded: "bg-stone-200 text-stone-700",
  };

  if (!b) return <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 text-stone-500">Loading…</div>;

  const currentIdx = STEPS.findIndex(s => s.key === b.status);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-6">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-stone-600 mb-4" data-testid="back-dashboard"><ArrowLeft size={16} /> Back to dashboard</Link>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden">
            <div className="h-[420px] md:h-[540px]">
              <MapView
                center={[b.provider_lat || b.customer_lat, b.provider_lng || b.customer_lng]}
                tracking={b.provider_lat ? b : null}
                markers={!b.provider_lat ? [{ lat: b.customer_lat, lng: b.customer_lng, label: "📍", popup: "You" }] : []}
                zoom={14}
                height="100%"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="bg-white border border-stone-200 rounded-3xl p-6">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Live tracking</div>
            <h2 className="heading text-2xl font-extrabold text-stone-900 mt-1" data-testid="booking-title">{b.listing_title}</h2>

            <div className="mt-4 p-4 bg-[#FFF3EC] rounded-2xl">
              <div className="flex items-center gap-2 text-sm text-stone-700"><Clock size={16} className="text-[#E05E36]" /> ETA</div>
              <div className="heading text-4xl font-extrabold text-[#E05E36] mt-1" data-testid="eta">
                {b.eta_min != null ? `${b.eta_min} min` : "—"}
              </div>
              <div className="text-xs text-stone-500 mt-1">Status: <b className="text-stone-900" data-testid="booking-status">{b.status.replace("_", " ")}</b></div>
            </div>

            {/* Steps */}
            <div className="mt-5 space-y-3">
              {STEPS.map((s, i) => {
                const done = i <= currentIdx;
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full grid place-items-center ${done ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-400"}`}>
                      <CheckCircle size={18} />
                    </div>
                    <div className={`text-sm font-semibold ${done ? "text-stone-900" : "text-stone-500"}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 border-t border-stone-200 pt-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#E05E36] text-white grid place-items-center font-bold">
                  {b.provider_name?.[0] || "P"}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-stone-900">{b.provider_name}</div>
                  <div className="text-xs text-stone-500">Provider</div>
                </div>
                {b.provider_mobile && (
                  <a href={`tel:${b.provider_mobile}`} className="w-10 h-10 rounded-full bg-[#2D5A27] text-white grid place-items-center" data-testid="call-provider">
                    <Phone size={18} />
                  </a>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="text-xs text-stone-500">Provider controls (demo)</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="rounded-full" onClick={() => setStatus("confirmed")} data-testid="btn-confirm">Confirm</Button>
                <Button size="sm" className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" onClick={() => setStatus("on_the_way", { provider_lat: b.provider_lat, provider_lng: b.provider_lng })} data-testid="btn-onway">On the way</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus("completed")} data-testid="btn-complete">Complete</Button>
              </div>
            </div>

            <div className="mt-5 border-t border-stone-200 pt-4 text-xs text-stone-600 space-y-1">
              <div><b>Charges:</b> ₹{Number(b.charges || 0).toLocaleString("en-IN")}</div>
              <div><b>Scheduled:</b> {new Date(b.scheduled_at).toLocaleString()}</div>
              <div><b>Address:</b> {b.customer_address}</div>
            </div>

            {/* Payment section */}
            <div className="mt-4 border-t border-stone-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
                  <Wallet size={13} /> Payment
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${payStatusStyle[b.payment_status] || "bg-stone-100"}`} data-testid="payment-status-badge">
                  {(b.payment_status || "unpaid").replace("_", " ")}
                </span>
              </div>
              <div className="bg-stone-50 rounded-xl p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-stone-500">Method</span><span className="font-bold text-stone-900">{payMethodLabel[b.payment_method] || b.payment_method}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Charges</span><span className="text-stone-800">₹{Number(b.charges || 0).toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Platform fee</span><span className="text-stone-800">+ ₹{Number(b.platform_fee || 0).toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between pt-1 border-t border-dashed border-stone-200"><span className="text-stone-500 font-bold">Total</span><span className="font-bold text-stone-900">₹{Number(b.total_amount || (b.charges + (b.platform_fee || 0))).toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Paid</span><span className="font-bold text-[#2D5A27]">₹{Number(b.amount_paid || 0).toLocaleString("en-IN")}</span></div>
                {((b.total_amount || b.charges + (b.platform_fee || 0)) - (b.amount_paid || 0)) > 0 && (
                  <div className="flex justify-between"><span className="text-stone-500">Remaining</span><span className="font-bold text-[#E05E36]">₹{Number((b.total_amount || b.charges + (b.platform_fee || 0)) - (b.amount_paid || 0)).toLocaleString("en-IN")}</span></div>
                )}
                {b.beneficiary_verified ? (
                  <div className="mt-2 pt-2 border-t border-stone-200 flex items-center gap-1.5 text-[10px] text-[#2D5A27] font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2D5A27]" /> Provider's genuine account verified · funds route safely
                  </div>
                ) : (
                  <div className="mt-2 pt-2 border-t border-stone-200 text-[10px] text-amber-700 font-bold">Provider has no verified account · prefer COD</div>
                )}
              </div>
              {b.payment_status !== "paid" && b.charges > 0 && (
                <Button onClick={markPaid} className="mt-3 w-full rounded-full bg-[#2D5A27] hover:bg-[#24491f] gap-2" data-testid="mark-paid-btn">
                  <IndianRupee size={16} />
                  {b.payment_method === "advance" ? "Pay advance now" : b.payment_method === "cod" ? "Mark cash received" : "Mark paid"}
                </Button>
              )}
              {b.payment_method === "advance" && b.payment_status === "unpaid" && (
                <div className="text-[11px] text-stone-500 mt-1 text-center">Demo mode: real gateway integration pending. Tap to simulate payment.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
